// Utils for Analytics

export const GA_TRACKING_ID = import.meta.env.VITE_GA_ID || 'G-XXXXXXXXXX';

// ---- Page View ----
export const pageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  }
};

// ---- Custom Event ----
export const event = (action: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, parameters);
  }
};

// ---- User Identification ----
// Sets user_id and user properties for GA4
// This allows you to see user-level data in GA4 reports
export const identifyUser = (user: {
  id: string | number;
  email?: string;
  name?: string;
  is_coach?: boolean;
}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  // Set user_id for cross-device tracking
  window.gtag('config', GA_TRACKING_ID, {
    user_id: String(user.id),
  });

  // Set user properties for segmentation in GA4
  window.gtag('set', 'user_properties', {
    user_role: user.is_coach ? 'coach' : 'customer',
    user_name: user.name || '',
  });
};

// Clear user identification on logout
export const clearUserIdentification = () => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', GA_TRACKING_ID, {
    user_id: undefined,
  });

  window.gtag('set', 'user_properties', {
    user_role: undefined,
    user_name: undefined,
  });
};

// ---- Auth Events ----
export const authEvents = {
  signup: (method: string = 'email') => {
    event('sign_up', {
      method,
    });
  },
  signin: (method: string = 'email') => {
    event('login', {
      method,
    });
  },
  logout: () => {
    event('logout');
    clearUserIdentification();
  },
};

// ---- Navigation Events ----
export const navigationEvents = {
  viewPage: (pageName: string, pageSection?: string) => {
    event('page_view_custom', {
      page_name: pageName,
      page_section: pageSection || 'general',
    });
  },
};

// ---- Coach Events ----
export const coachEvents = {
  createPlan: (planId?: string) => {
    event('create_plan', { plan_id: planId });
  },
  createWorkout: (workoutId?: string) => {
    event('create_workout', { workout_id: workoutId });
  },
  createExercise: (exerciseId?: string) => {
    event('create_exercise', { exercise_id: exerciseId });
  },
  inviteCustomer: () => {
    event('invite_customer');
  },
  publishPlan: (planId?: string) => {
    event('publish_plan', { plan_id: planId });
  },
};

// ---- Customer Events ----
export const customerEvents = {
  viewWorkout: (workoutId?: string) => {
    event('view_workout', { workout_id: workoutId });
  },
  editExercise: (exerciseId?: string) => {
    event('edit_exercise', { exercise_id: exerciseId });
  },
};
